import { Clock } from 'lucide-react'
import { cn } from '@workspace/ui/lib/utils'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import type { Notification } from '@workspace/lib/api/notification'
import { formatActivityTime } from '@/shared/lib/utils'
import { NotificationContent } from './NotificationContent'
import { useUser } from '@/shared/store/auth.store'

interface NotificationItemProps {
    notification: Notification
    onClick?: () => void
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
    const currentUser = useUser()
    const isUnread = !notification.isRead

    const isAvatarNotification = [
        'new_request_approval',
        'request_approval_rejected',
        'request_approval_approved',
        'task_comment',
        'project_item_changed',
    ].includes(notification.type)

    const isWarning = notification.type === 'task_neardue' || notification.type === 'task_overdue'

    const isTaskAssigned = notification.type === 'task_assigned'

    const isStillAssigned = notification.task?.assignedUsers?.some(u => u.id === currentUser?.id) ?? false
    const isNotStarted = notification.task?.status === 'todo' || notification.task?.status === 'receiving'

    const shouldHighlightAssignedTask = isTaskAssigned && isStillAssigned && isNotStarted

    const displayTitle = isTaskAssigned ? notification.title.split(':')[0] : notification.title

    const actor =
        notification.type === 'task_comment'
            ? notification.requester
            : notification.metadata?.createdBy || notification.requester

    return (
        <div
            className={cn(
                'flex gap-2 px-4 py-3 cursor-pointer hover:bg-background-muted/5 transition-colors',
                isWarning && 'hover:bg-background-overdue',
                shouldHighlightAssignedTask && 'bg-yellow-50 hover:bg-yellow-100',
            )}
            onClick={onClick}
        >
            {isAvatarNotification && actor && (
                <UserAvatar
                    src={actor.avatar}
                    alt={actor.fullName}
                    fallback={actor.fullName}
                    size="md"
                    className="shrink-0"
                />
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
                {/* Title + Unread dot */}
                <div className="flex items-start justify-between gap-2">
                    <p
                        className={cn(
                            'text-sm font-semibold text-foreground line-clamp-1 flex-1',
                            shouldHighlightAssignedTask && 'font-bold text-red-600',
                        )}
                        title={displayTitle}
                    >
                        {displayTitle}
                    </p>
                    {isUnread && <span className="size-2 rounded-full bg-primary shrink-0" />}
                </div>

                {/* Content description */}
                <p className="text-xs text-neutral-500 line-clamp-3 mt-1 leading-4">
                    <NotificationContent notification={notification} />
                </p>

                {/* Time with clock icon */}
                <div className={cn('flex items-center gap-1 mt-1 text-neutral-500', isAvatarNotification && 'mt-2')}>
                    <Clock className="size-4!" />
                    <p className="text-xs">{formatActivityTime(notification.createdAt)}</p>
                </div>
            </div>
        </div>
    )
}
